import {
  applyIfNewer,
  settingsApi,
  type SettingsFrame,
  type UserParametersFrame,
} from '#entities';

import type { AppDispatch, RootState } from '#shared';

interface CacheEntry {
  data?: unknown;
  isLoading: boolean;
}

const writeFrame = (
  entry: CacheEntry,
  patch: () => void,
  upsert: () => void,
): void => {
  if (entry.data !== undefined) {
    patch();
    return;
  }
  // `updateQueryData` is a no-op on an entry without data. Upserting instead
  // lets `merge` hold the in-flight GET to this frame's ts when it lands.
  if (entry.isLoading) {
    upsert();
  }
};

const refetchUserParameters = async (dispatch: AppDispatch): Promise<void> => {
  // A forced initiate is skipped while a request for the key is pending. That
  // request may have been answered before the global change.
  await dispatch(
    settingsApi.util.getRunningQueryThunk('getUserParameters', undefined),
  );
  await dispatch(
    settingsApi.endpoints.getUserParameters.initiate(undefined, {
      subscribe: false,
      forceRefetch: true,
    }),
  );
};

export const applySettingsFrame =
  ({ settings, ts }: SettingsFrame) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const snapshot = { settings, ts };
    writeFrame(
      settingsApi.endpoints.getGlobalSettings.select()(getState()),
      () =>
        dispatch(
          settingsApi.util.updateQueryData(
            'getGlobalSettings',
            undefined,
            applyIfNewer(snapshot),
          ),
        ),
      () =>
        void dispatch(
          settingsApi.util.upsertQueryData(
            'getGlobalSettings',
            undefined,
            snapshot,
          ),
        ),
    );
    const user = settingsApi.endpoints.getUserParameters.select()(getState());
    if (user.data === undefined && !user.isLoading) {
      return;
    }
    // The frame carries the global tier only, and a global change can alter
    // what a user parameter resolves to, so a held user tier is refetched.
    void refetchUserParameters(dispatch);
  };

export const applyUserParametersFrame =
  ({ parameters, ts }: UserParametersFrame) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const snapshot = { parameters, ts };
    writeFrame(
      settingsApi.endpoints.getUserParameters.select()(getState()),
      () =>
        dispatch(
          settingsApi.util.updateQueryData(
            'getUserParameters',
            undefined,
            applyIfNewer(snapshot),
          ),
        ),
      () =>
        void dispatch(
          settingsApi.util.upsertQueryData(
            'getUserParameters',
            undefined,
            snapshot,
          ),
        ),
    );
  };
